import { useState, useEffect, FC } from "react";
import { RouteComponentProps } from "react-router";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import TableComponent from "../../components/TableComponent";
import UserCrewRow from "../../components/UserCrewRow";

import { getMembersList } from "../../api/crew";
import { formatLastFirstName } from "../../services/formatters";
import useCrew from "../../hooks/useCrew";
import type { UserWithRoles } from "../../shared/api-types/user";

interface MatchParams {
  crewId: string;
}

type UsersListProps = RouteComponentProps<MatchParams>;

const UsersList: FC<UsersListProps> = ({ match }) => {
  const { t } = useTranslation();
  const crewIdParam = match?.params?.crewId;
  const crewId = Number(crewIdParam);
  const { crew } = useCrew();
  const isValidCrewId = !isNaN(crewId) && crewId >= 1;
  const [users, setUsers] = useState<UserWithRoles[]>([]);

  useEffect(() => {
    if (!isValidCrewId) return;
    (async () => {
      const res = (await getMembersList(crewId, crew?.baseIds)).sort((a, b) =>
        formatLastFirstName(a?.fullname).localeCompare(formatLastFirstName(b?.fullname)),
      );
      setUsers(res);
    })();
  }, [crewId, isValidCrewId, crew?.baseIds]);

  if (!isValidCrewId) {
    return (
      <Typography variant="h6" color="error">
        {t("invalid_crew_id", { crewId: crewIdParam })}
      </Typography>
    );
  }

  return (
    <div className="table-wrapper">
      <Typography
        variant="h4"
        className="t-none"
        sx={{
          fontSize: "small",
        }}
      >
        {t("users")}
      </Typography>
      <TableComponent
        header={[
          { title: t("table_headers.id"), sort: "id", style: { width: "5px", paddingRight: 0 } },
          { title: t("table_headers.full_name"), sort: "fullname" },
          { title: t("table_headers.email"), sort: "email" },
          {
            title: t("table_headers.country"),
            sort: "country",
            style: { width: "5px", paddingRight: 0 },
          },
        ]}
        renderChildren={(user: UserWithRoles) => <UserCrewRow key={user.id} user={user} />}
        items={users}
        search={["fullname", "id", "email"]}
        pagination
        users
      />
    </div>
  );
};

export default UsersList;
